import { useTranslations } from '@vocab/react';
import { Button, Stack, Text } from 'braid-design-system';
import { useCallback, useEffect } from 'react';

import { useHotjarEvent } from 'src/hooks/useHotjarEvent';

import { CareerFeedMobileWebFeatureFlagContainer } from '../CareerFeedMobileWebFeatureFlagContainer';
import {
  useGetResetCandidateChatContext,
  useGetRefreshRecommendationsWithCandidateChatContext,
} from '../hooks/candidateChatContextHelpers';
import { useCareerFeedAIAssistant } from '../hooks/useCareerFeedAIAssistant';

import translations from './.vocab';
import {
  CareerFeedAIAssistantContentFrame,
  CareerFeedAIAssistantContentFrameMobile,
} from './CareerFeedAIAssistantContentFrame';
import { RecommendationRefinedConfirmation } from './CareerFeedAIAssistantRecommendationRefinedConfirmation';
import { CareerFeedMobileModal } from './CareerFeedMobileModal';

export const ConversationFinished = () => {
  const resetPreferences = useGetResetCandidateChatContext();
  const {
    lastCompletedAssistantResponse,
    tracking: {
      trackRecommendedJobsRefreshed,
      trackGenAIChatEnded,
      trackGenAIChatResetPressed,
    },
    closeMobileModal,
  } = useCareerFeedAIAssistant();
  const { t } = useTranslations(translations);

  const refreshRecommendations =
    useGetRefreshRecommendationsWithCandidateChatContext();

  const refreshRecs = useCallback(async () => {
    const result = await refreshRecommendations();
    if (result.success) {
      trackGenAIChatEnded();
      trackRecommendedJobsRefreshed({ jobsCount: result.jobsCount });
    } else {
      trackGenAIChatEnded(Boolean(result.error));
      trackRecommendedJobsRefreshed({ jobsCount: -1 });
    }
  }, [
    refreshRecommendations,
    trackGenAIChatEnded,
    trackRecommendedJobsRefreshed,
  ]);

  useHotjarEvent('homepage_genAI_end');

  useEffect(() => {
    refreshRecs();
    // Ensuring that this will get called once. Having refreshRecs
    // as does trigger refresh multiple times
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onReset = useCallback(() => {
    trackGenAIChatResetPressed('chat widget');
    resetPreferences();
  }, [resetPreferences, trackGenAIChatResetPressed]);

  const message = lastCompletedAssistantResponse?.text ?? '';

  return (
    <CareerFeedMobileWebFeatureFlagContainer
      mobileView={
        <>
          <CareerFeedMobileModal>
            <CareerFeedAIAssistantContentFrameMobile
              headMessage={message}
              errorType={null}
              contentSection={<></>}
              ctaSection={
                <Button
                  variant="solid"
                  tone="brandAccent"
                  onClick={closeMobileModal}
                >
                  {t('Discover your recommendations')}
                </Button>
              }
              loading={
                false /* Loading is just used for loading overlay, we don't want this on the final q */
              }
            />
          </CareerFeedMobileModal>
          <RecommendationRefinedConfirmation />
        </>
      }
      tabletAndAboveView={
        <CareerFeedAIAssistantContentFrame
          headMessage={message}
          ctaSection={
            <Stack space="small">
              <Button onClick={onReset}>{t('Try different answers')}</Button>
              <Text size="xsmall" tone="secondary">
                {t('These AI-powered job suggestions....')}
              </Text>
            </Stack>
          }
          loading={
            false /* Loading is just used for loading overlay, we don't want this on the final q */
          }
        />
      }
    />
  );
};
